import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import { Button } from "@/components/ui/button";
import {
  Activity,
  Bell,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Send,
  ShieldAlert,
  TerminalSquare,
} from "lucide-react";

// Admin → Operations → System Health
//
// Surfaces the self-healing Playwright bootstrap so the team can confirm
// chromium-headless-shell presence and re-trigger the heal on demand
// without SSH. Backed by:
//   GET  /api/admin/diagnostics/playwright
//   POST /api/admin/diagnostics/playwright/heal
//
// (Slack/Discord drift alerts will hook into the same endpoints — keep
// the response shape stable.)

const HEALTHY_BADGE = "bg-emerald-500/10 text-emerald-300 border-emerald-500/30";
const UNHEALTHY_BADGE = "bg-rose-500/10 text-rose-300 border-rose-500/30";

function StatusPill({ ok, labelOk = "Healthy", labelBad = "Missing" }) {
  const cls = ok ? HEALTHY_BADGE : UNHEALTHY_BADGE;
  return (
    <span
      data-testid={ok ? "playwright-status-ok" : "playwright-status-bad"}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${cls}`}
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
      {ok ? labelOk : labelBad}
    </span>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/5 py-2.5 last:border-b-0">
      <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
      <span
        data-testid={testId}
        className="max-w-[60%] break-all text-right text-sm font-mono text-slate-200"
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

export default function AdminSystemHealth() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healing, setHealing] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [testingWebhook, setTestingWebhook] = useState(false);

  const fetchDiag = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get("/admin/diagnostics/playwright");
      setDiag(r.data);
      setLastChecked(new Date());
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load Playwright diagnostics"));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchWebhooks = useCallback(async () => {
    try {
      const r = await api.get("/admin/drift-webhooks/config");
      setWebhooks(r.data);
    } catch (e) {
      // non-fatal — section just won't render pills
      setWebhooks({ slack: false, discord: false });
    }
  }, []);

  useEffect(() => {
    fetchDiag();
    fetchWebhooks();
    const t = setInterval(() => fetchDiag(true), 60_000);
    return () => clearInterval(t);
  }, [fetchDiag, fetchWebhooks]);

  const triggerHeal = async () => {
    setHealing(true);
    try {
      const r = await api.post("/admin/diagnostics/playwright/heal");
      const ok = r.data?.ok;
      toast[ok ? "success" : "error"](
        ok
          ? `Chromium v${r.data?.expected_revision} is present.`
          : "Self-heal completed but the binary is still missing — check backend logs.",
      );
      await fetchDiag(true);
    } catch (e) {
      toast.error(formatApiError(e, "Self-heal failed"));
    } finally {
      setHealing(false);
    }
  };

  const sendTestAlert = async () => {
    setTestingWebhook(true);
    try {
      const r = await api.post("/admin/drift-webhooks/test");
      const data = r.data || {};
      if (data.reason === "no_webhooks_configured") {
        toast.warning(
          "No webhooks configured. Set SLACK_WEBHOOK_URL or DISCORD_WEBHOOK_URL in backend/.env and restart the backend.",
        );
      } else if (data.posted) {
        const targets = Object.entries(data.receipts || {})
          .filter(([, ok]) => ok)
          .map(([name]) => name)
          .join(" + ");
        toast.success(`Test alert delivered to ${targets || "configured webhook"}.`);
      } else {
        toast.error(
          "Webhook URL reachable but returned a non-2xx — check the backend logs.",
        );
      }
    } catch (e) {
      toast.error(formatApiError(e, "Test alert failed"));
    } finally {
      setTestingWebhook(false);
    }
  };

  const ok = diag?.ok === true;

  return (
    <ToolPageShell
      eyebrow="Operations"
      title="System Health"
      intro="Live status of the headless browser pipeline that powers demo videos, marketing reels, onboarding-packet PDFs, and tech-stack docs. The bootstrap auto-heals on boot — use the manual trigger when you have evidence a wipe just happened."
      actions={
        <Button
          data-testid="system-health-refresh-btn"
          variant="outline"
          onClick={() => fetchDiag()}
          disabled={loading || healing}
        >
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <section
          data-testid="playwright-health-card"
          className="lg:col-span-2 rounded-2xl border border-white/10 bg-slate-950/40 p-6 backdrop-blur"
        >
          <header className="mb-4 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                <Activity className="h-4 w-4 text-sky-400" />
                Playwright · chromium-headless-shell
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Required by demo/marketing video renderers and the onboarding-packet PDF generator.
              </p>
            </div>
            {diag && <StatusPill ok={ok} />}
          </header>

          {loading && !diag ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking binary…
            </div>
          ) : diag ? (
            <div className="rounded-xl border border-white/5 bg-slate-900/40 p-4">
              <Row
                label="Expected revision"
                value={diag.expected_revision}
                testId="playwright-revision"
              />
              <Row label="Browsers root" value={diag.browsers_path} />
              <Row label="Binary path" value={diag.binary_path} testId="playwright-binary-path" />
              <Row
                label="Binary present"
                value={diag.binary_present ? "yes" : "no"}
                testId="playwright-binary-present"
              />
              <Row
                label="Binary executable"
                value={diag.binary_executable ? "yes" : "no"}
              />
              {diag.reason && <Row label="Reason" value={diag.reason} />}
            </div>
          ) : (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-200">
              Could not load diagnostics. Check the backend logs.
            </div>
          )}

          {lastChecked && (
            <p className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">
              Last checked {lastChecked.toLocaleTimeString()} · auto-refreshes every 60s
            </p>
          )}
        </section>

        <aside className="rounded-2xl border border-white/10 bg-slate-950/40 p-6 backdrop-blur">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
            <TerminalSquare className="h-4 w-4 text-amber-400" />
            Manual self-heal
          </div>
          <p className="mt-2 text-xs text-slate-400">
            Re-runs <code className="rounded bg-slate-900/80 px-1 py-0.5">playwright install chromium-headless-shell</code> at the revision Playwright expects. Idempotent — safe to click any time.
          </p>
          <Button
            data-testid="playwright-heal-btn"
            className="mt-4 w-full"
            onClick={triggerHeal}
            disabled={healing || loading}
          >
            {healing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Healing…
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Re-install now
              </>
            )}
          </Button>
          {ok && !healing && (
            <p className="mt-3 text-[11px] text-emerald-400/80">
              Binary already present — clicking will be a no-op (~50ms).
            </p>
          )}
        </aside>
      </div>

      <section
        data-testid="drift-webhooks-card"
        className="mt-6 rounded-2xl border border-white/10 bg-slate-950/40 p-6 backdrop-blur"
      >
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
              <Bell className="h-4 w-4 text-violet-400" />
              Drift alert webhooks
            </div>
            <p className="mt-1 text-xs text-slate-400">
              When the daily digest detects a system-health or conversion-rate drift, Nodera also
              pings any configured Slack / Discord webhook. Configure URLs via{" "}
              <code className="rounded bg-slate-900/80 px-1 py-0.5 text-[11px]">SLACK_WEBHOOK_URL</code>{" "}
              and{" "}
              <code className="rounded bg-slate-900/80 px-1 py-0.5 text-[11px]">DISCORD_WEBHOOK_URL</code>{" "}
              in <code className="rounded bg-slate-900/80 px-1 py-0.5 text-[11px]">backend/.env</code>.
            </p>
          </div>
          <Button
            data-testid="drift-webhooks-test-btn"
            variant="outline"
            onClick={sendTestAlert}
            disabled={testingWebhook}
          >
            {testingWebhook ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send test alert
          </Button>
        </header>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-xl border border-white/5 bg-slate-900/40 p-4">
            <span className="text-sm font-medium text-slate-200">Slack</span>
            <StatusPill
              ok={!!webhooks?.slack}
              labelOk="Configured"
              labelBad="Not configured"
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-white/5 bg-slate-900/40 p-4">
            <span className="text-sm font-medium text-slate-200">Discord</span>
            <StatusPill
              ok={!!webhooks?.discord}
              labelOk="Configured"
              labelBad="Not configured"
            />
          </div>
        </div>
      </section>
    </ToolPageShell>
  );
}
